/**
 * RackPriceChangeCard — Orders workspace · v1.2.2 · 2026-08-19.
 *
 * A compact 4-tile card showing today's approved rack-price change
 * per product. Sits above the Orders table.
 *
 * IMPORTANT: this card is a read-only view of the SAME approved
 * price-notification record that was sent to drivers. It never
 * introduces a second source of pricing data.
 *
 * Data comes from `GET /api/daily-pricing/rack/change-summary?date=…`
 * which reads from the approved `price_notifications` record for the
 * requested effective_date. The frontend passes a business date built
 * from the local Toronto calendar (getFullYear/getMonth/getDate) so
 * we never introduce UTC rollover.
 */
import { useEffect, useState } from "react";
import { ArrowUp, ArrowDown, Minus, Spinner } from "@phosphor-icons/react";
import { api } from "@/lib/api";


// PO-approved 4-tile order. DEF intentionally omitted.
const PRODUCT_LABELS_FALLBACK = [
  { code: "regular",      label: "Regular" },
  { code: "premium",      label: "Premium" },
  { code: "clear_diesel", label: "Clear Diesel" },
  { code: "dyed_diesel",  label: "Dyed Diesel" },
];


function fmtBusinessDate(iso) {
  // "2026-08-19" → "Aug 19, 2026". Do NOT construct new Date(iso) —
  // that parses YYYY-MM-DD as UTC midnight and the Toronto operator
  // sees the previous day. Parse the ISO date parts manually.
  if (!iso || iso.length !== 10) return iso || "";
  const [y, m, d] = iso.split("-").map((s) => parseInt(s, 10));
  if (!y || !m || !d) return iso;
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${months[m - 1]} ${d}, ${y}`;
}

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString("en-CA", {
      hour: "numeric", minute: "2-digit", timeZone: "America/Toronto",
    }).toLowerCase().replace(" ", "\u00a0");
  } catch { return "—"; }
}


function DirectionGlyph({ direction, size = 14 }) {
  if (direction === "up")   return <ArrowUp   size={size} weight="bold" />;
  if (direction === "down") return <ArrowDown size={size} weight="bold" />;
  return <Minus size={size} weight="bold" />;
}


function fmtChange(change) {
  // Always 4 decimal places, always signed by the arrow (not by "+"/"-").
  if (change === null || change === undefined || change === "") return "0.0000";
  const n = Math.abs(Number(change));
  if (Number.isNaN(n)) return "0.0000";
  return n.toFixed(4);
}


function directionClasses(direction) {
  if (direction === "up")   return "text-rose-700";       // increase = cost up
  if (direction === "down") return "text-emerald-700";    // decrease = cost down
  return "text-slate-500";
}


function ProductTile({ product }) {
  const change = fmtChange(product.change);
  const dir = product.direction || "flat";
  const cls = directionClasses(dir);
  return (
    <div
      data-testid={`rack-change-tile-${product.code}`}
      className="flex-1 min-w-0 px-4 py-3 first:pl-0 last:pr-0 border-r border-slate-100 last:border-r-0"
    >
      <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500 truncate">
        {product.label}
      </div>
      <div className={`mt-1 flex items-center gap-1.5 ${cls}`}>
        <DirectionGlyph direction={dir} size={14} />
        <span
          data-testid={`rack-change-value-${product.code}`}
          className="text-[15px] font-semibold tabular-nums"
        >
          ${change}/L
        </span>
      </div>
    </div>
  );
}


export default function RackPriceChangeCard({ businessDate, dataTestId = "rack-price-change-card" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!businessDate) return;
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await api.get("/daily-pricing/rack/change-summary", {
          params: { date: businessDate },
        });
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Could not load rack change.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [businessDate]);

  const products = data?.products || PRODUCT_LABELS_FALLBACK.map((p) => ({ ...p, change: null, direction: null }));

  return (
    <div
      data-testid={dataTestId}
      className="rounded-lg border border-slate-200 bg-white shadow-sm px-4 py-3 w-full sm:min-w-[520px] sm:max-w-[640px]"
    >
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-sm font-semibold text-slate-900">Today's Rack Price Change</h3>
        <span
          data-testid="rack-change-business-date"
          className="text-xs text-slate-500 tabular-nums"
        >
          {data?.is_carried_forward && data?.effective_from
            ? `Effective from ${fmtBusinessDate(data.effective_from)} · still current`
            : fmtBusinessDate(businessDate)}
        </span>
      </div>

      {loading ? (
        <div className="mt-3 flex items-center gap-2 text-sm text-slate-500">
          <Spinner size={14} className="animate-spin" /> Loading…
        </div>
      ) : error ? (
        <div className="mt-3 text-sm text-rose-700" data-testid="rack-change-error">
          {error}
        </div>
      ) : !data?.approved ? (
        <div
          data-testid="rack-change-not-approved"
          className="mt-3 rounded border border-dashed border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-600"
        >
          Rack prices not yet approved
        </div>
      ) : (
        <>
          <div className="mt-2 grid grid-cols-2 sm:flex sm:divide-x sm:divide-slate-100" data-testid="rack-change-tiles">
            {products.map((p) => (
              <ProductTile key={p.code} product={p} />
            ))}
          </div>
          <div
            data-testid="rack-change-footer"
            className="mt-2 pt-2 border-t border-slate-100 text-xs text-slate-500"
          >
            <span>Approved {fmtTime(data.approved_at)}</span>
            {data.notification?.status === "sent" ? (
              <>
                <span className="mx-1.5">·</span>
                <span data-testid="rack-change-notif-sent">
                  Driver notice sent {fmtTime(data.notification.sent_at)}
                </span>
                {data.notification.recipient_count != null && (
                  <>
                    <span className="mx-1.5">·</span>
                    <span data-testid="rack-change-notif-recipients">
                      {data.notification.recipient_count} recipient{data.notification.recipient_count === 1 ? "" : "s"}
                    </span>
                  </>
                )}
              </>
            ) : (
              <>
                <span className="mx-1.5">·</span>
                <span data-testid="rack-change-notif-pending" className="text-amber-700 font-medium">
                  Driver notice pending
                </span>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
